<template>
  <transition name="fn-fade">
    <div :class="['fn-card', { 'fn-card--coming-soon': isComingSoon }]" v-if="visible && nudges.length > 0"
      @mouseenter="pauseRotation" @mouseleave="resumeRotation">
      <div :class="['fn-card__body', slidePhase ? 'fn-card__body--' + slidePhase : '']">
        <div class="fn-card__label">
          <i class="fa fa-lightbulb-o"></i>
          {{ isComingSoon ? 'Coming Soon' : 'Tip' }}
        </div>
        <div class="fn-card__icon">
          <i :class="'fa ' + currentNudge.icon"></i>
        </div>
        <div class="fn-card__content">
          <h4 class="fn-card__title">{{ currentNudge.title }}</h4>
          <p class="fn-card__text">{{ currentNudge.text }}</p>
        </div>
        <div class="fn-card__actions">
          <router-link v-if="!isComingSoon" :to="currentNudge.ctaRoute" class="fn-card__cta" @click.native="onCtaClick">
            {{ currentNudge.ctaText }}
          </router-link>
          <span v-else class="fn-card__cta fn-card__cta--soon">
            <i class="fa fa-clock-o mr-1"></i> Stay Tuned
          </span>
          <div class="fn-card__dismiss-wrap">
            <button class="fn-card__dismiss" @click="showDismissMenu = !showDismissMenu" title="Dismiss options">
              <i class="fa fa-times"></i>
            </button>
            <div class="fn-card__dismiss-menu" v-if="showDismissMenu" @mouseleave="showDismissMenu = false">
              <button @click="dismissCurrent">Dismiss this tip</button>
              <button @click="dismissAllOnPage">Don't show tips on this page</button>
            </div>
          </div>
        </div>
      </div>
      <div class="fn-card__nav" v-if="nudges.length > 1">
        <button class="fn-card__arrow" @click="manualPrev" :disabled="currentIndex === 0">
          <i class="fa fa-chevron-left"></i>
        </button>
        <div class="fn-card__dots">
          <span
            v-for="(n, i) in nudges"
            :key="n.id"
            class="fn-card__dot"
            :class="{ 'fn-card__dot--active': i === currentIndex }"
            @click="manualGoTo(i)"
          ></span>
        </div>
        <button class="fn-card__arrow" @click="manualNext" :disabled="currentIndex === nudges.length - 1">
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions } from 'vuex';

const ROTATE_INTERVAL = 8000; // 8 seconds per nudge

export default {
  name: 'FeatureNudge',
  data() {
    return {
      visible: false,
      currentIndex: 0,
      delayTimer: null,
      rotateTimer: null,
      manualMode: false,
      hovered: false,
      slidePhase: '',
      showDismissMenu: false,
    };
  },
  computed: {
    nudges() {
      const user = this.$store.state.customer.user;
      if (!user) return [];
      return this.$store.getters['nudges/nudgesForPage']({
        route: this.$route.path,
        role: user.Role,
        siteID: user.SiteID || user.siteID,
      });
    },
    currentNudge() {
      return this.nudges[this.currentIndex] || {};
    },
    isComingSoon() {
      return this.currentNudge.type === 'coming_soon';
    },
  },
  watch: {
    '$route.path'() {
      this.currentIndex = 0;
      this.manualMode = false;
      this.stopRotation();
      this.scheduleShow();
    },
    nudges(newVal) {
      if (this.currentIndex >= newVal.length) {
        this.currentIndex = Math.max(0, newVal.length - 1);
      }
      if (newVal.length === 0) {
        this.visible = false;
        this.stopRotation();
      }
    },
  },
  mounted() {
    const loaded = this.$store.state.nudges.loaded;
    if (!loaded) {
      this.$store.dispatch('nudges/fetchNudges').then(() => this.scheduleShow());
    } else {
      this.scheduleShow();
    }
  },
  beforeDestroy() {
    clearTimeout(this.delayTimer);
    this.stopRotation();
  },
  methods: {
    ...mapActions('nudges', ['dismissNudge']),
    scheduleShow() {
      this.visible = false;
      clearTimeout(this.delayTimer);

      const lastCtaClick = parseInt(localStorage.getItem('teamio-nudge-cta-ts') || '0', 10);
      const elapsed = Date.now() - lastCtaClick;
      const suppressMs = 30000;
      const delay = elapsed < suppressMs ? (suppressMs - elapsed) : 300;

      this.delayTimer = setTimeout(() => {
        if (this.nudges.length > 0) {
          this.visible = true;
          this.startRotation();
        }
      }, delay);
    },

    // Auto-rotation
    slideTo(newIndex) {
      if (newIndex === this.currentIndex || this.slidePhase) return;
      // Phase 1: slide current content out to the left
      this.slidePhase = 'out';
      setTimeout(() => {
        // Phase 2: swap content, position off-screen right, then slide in
        this.currentIndex = newIndex;
        this.slidePhase = 'in';
        // Force reflow so the 'in' starting position applies before transition
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.slidePhase = '';
          });
        });
      }, 350);
    },
    startRotation() {
      this.stopRotation();
      if (this.manualMode || this.nudges.length <= 1) return;
      this.rotateTimer = setInterval(() => {
        if (this.hovered) return;
        const next = (this.currentIndex + 1) % this.nudges.length;
        this.slideTo(next);
      }, ROTATE_INTERVAL);
    },
    stopRotation() {
      clearInterval(this.rotateTimer);
      this.rotateTimer = null;
    },
    pauseRotation() {
      this.hovered = true;
    },
    resumeRotation() {
      this.hovered = false;
    },

    // Manual navigation — stops auto-rotation permanently
    manualPrev() {
      this.manualMode = true;
      this.stopRotation();
      if (this.currentIndex > 0) this.slideTo(this.currentIndex - 1);
    },
    manualNext() {
      this.manualMode = true;
      this.stopRotation();
      if (this.currentIndex < this.nudges.length - 1) this.slideTo(this.currentIndex + 1);
    },
    manualGoTo(i) {
      this.manualMode = true;
      this.stopRotation();
      this.slideTo(i);
    },

    dismissCurrent() {
      this.showDismissMenu = false;
      const nudge = this.currentNudge;
      if (!nudge || !nudge.id) return;
      // Pass the full nudge — the store derives a stable dismissKey from
      // its title so the dismissal survives re-seeds that change doc IDs.
      this.dismissNudge(nudge);
      if (this.currentIndex >= this.nudges.length) {
        this.currentIndex = Math.max(0, this.nudges.length - 1);
      }
    },
    dismissAllOnPage() {
      this.showDismissMenu = false;
      this.stopRotation();
      // Dismiss every nudge currently showing on this page
      this.nudges.forEach(n => {
        if (n.id) this.dismissNudge(n);
      });
    },
    onCtaClick() {
      localStorage.setItem('teamio-nudge-cta-ts', Date.now().toString());
      this.dismissCurrent();
    },
  },
};
</script>

<style lang="scss" scoped>
$green: #3abf94;
$green-dark: #2d9474;
$text-primary: #0f172a;
$text-secondary: #475569;
$text-muted: #94a3b8;

.fn-card {
  margin: 0 0 12px;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdf4 100%);
  border: 1px solid rgba($green, 0.2);
  border-left: 4px solid $green;
  border-radius: 10px;
  overflow: visible;
}

.fn-card__body {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  transition: transform 0.35s ease, opacity 0.3s ease;

  // Slide out to left
  &--out {
    transform: translateX(-100%);
    opacity: 0;
  }

  // Start position for slide in (off-screen right, no transition)
  &--in {
    transform: translateX(100%);
    opacity: 0;
    transition: none;
  }
}

.fn-card__label {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: $green-dark;
  flex-shrink: 0;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  padding: 2px 0;

  .fa { font-size: 11px; }
}

.fn-card__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $green;
  font-size: 14px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.fn-card__content {
  flex: 1;
  min-width: 0;
}

.fn-card__title {
  font-size: 13px;
  font-weight: 600;
  color: $text-primary;
  margin: 0;
  line-height: 1.3;
}

.fn-card__text {
  font-size: 11px;
  color: $text-secondary;
  margin: 0;
  line-height: 1.4;
}

.fn-card__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.fn-card__cta {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  background: $green;
  color: white;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    background: $green-dark;
    color: white;
    text-decoration: none;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba($green, 0.3);
  }
}

.fn-card__dismiss-wrap {
  position: relative;
  flex-shrink: 0;
}

.fn-card__dismiss {
  background: none;
  border: none;
  color: $text-muted;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1;

  &:hover {
    color: $text-secondary;
    background: rgba(0, 0, 0, 0.05);
  }
}

.fn-card__dismiss-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  overflow: hidden;
  min-width: 200px;

  button {
    display: block;
    width: 100%;
    padding: 8px 14px;
    border: none;
    background: none;
    text-align: left;
    font-size: 12px;
    color: $text-secondary;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background: #f8fafc;
      color: $text-primary;
    }

    & + button {
      border-top: 1px solid #f1f5f9;
    }
  }
}

.fn-card__nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 14px 6px;
}

.fn-card__arrow {
  background: none;
  border: none;
  color: $text-muted;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 9px;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    color: $green;
    background: rgba($green, 0.08);
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
}

.fn-card__dots {
  display: flex;
  gap: 4px;
}

.fn-card__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #cbd5e1;
  cursor: pointer;
  transition: all 0.2s;

  &--active {
    background: $green;
    width: 14px;
    border-radius: 3px;
  }

  &:hover:not(.fn-card__dot--active) {
    background: #94a3b8;
  }
}

// Coming Soon variant — purple theme
$purple: #7c3aed;

.fn-card--coming-soon {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #f5f3ff 100%);
  border-color: rgba($purple, 0.2);
  border-left-color: $purple;

  .fn-card__label { color: #6d28d9; }
  .fn-card__icon { color: $purple; }
  .fn-card__dot--active { background: $purple; }
  .fn-card__arrow:hover:not(:disabled) {
    color: $purple;
    background: rgba($purple, 0.08);
  }
}

.fn-card__cta--soon {
  background: #ddd6fe;
  color: $purple;
  cursor: default;
  pointer-events: none;
}

// Animation — slow slide from right to left
.fn-fade-enter-active {
  animation: fn-slide-in 0.8s ease-out;
}
.fn-fade-leave-active {
  animation: fn-slide-out 0.4s ease-in forwards;
}
@keyframes fn-slide-in {
  0% {
    opacity: 0;
    transform: translateX(100%);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}
@keyframes fn-slide-out {
  0% {
    opacity: 1;
    transform: translateX(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-100%);
  }
}

// Mobile
@media (max-width: 768px) {
  .fn-card__body {
    flex-wrap: wrap;
    gap: 8px;
  }

  .fn-card__label {
    writing-mode: horizontal-tb;
    transform: none;
    width: 100%;
  }

  .fn-card__content {
    flex-basis: 100%;
  }

  .fn-card__actions {
    width: 100%;
  }

  .fn-card__cta {
    flex: 1;
    justify-content: center;
  }
}
</style>
